import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Button,
  Chip
} from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import { partService } from '../../services/partService';
import { labelTextColor } from '../PartLabelChips';

/**
 * Admin: list and remove part labels (colored chips catalog).
 */
const PartLabelsManager = () => {
  const [labels, setLabels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [removing, setRemoving] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const res = await partService.getLabels();
      setLabels(Array.isArray(res?.data) ? res.data : []);
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.message || 'Failed to load part labels');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleRemove = async (lab) => {
    if (!window.confirm(`Remove label "${lab.name}"? It will be removed from all parts.`)) {
      return;
    }
    try {
      setRemoving(lab.id);
      setError('');
      setSuccess('');
      await partService.removeLabel(lab.id);
      setSuccess(`Removed "${lab.name}".`);
      await load();
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.message || 'Failed to remove label');
    } finally {
      setRemoving(null);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <CircularProgress size={20} />
        <Typography variant="body2" color="text.secondary">
          Loading labels…
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 1 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 1 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Labels are colored chips on the Parts list. Create them when editing a part (New Label…). Admins can remove them here.
      </Typography>
      {labels.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No labels yet.
        </Typography>
      ) : (
        <List dense disablePadding>
          {labels.map((lab) => (
            <ListItem
              key={lab.id}
              secondaryAction={
                <IconButton
                  edge="end"
                  aria-label={`Remove ${lab.name}`}
                  onClick={() => handleRemove(lab)}
                  disabled={removing != null}
                  size="small"
                >
                  {removing === lab.id ? <CircularProgress size={18} /> : <DeleteIcon fontSize="small" />}
                </IconButton>
              }
              sx={{ borderBottom: '1px solid', borderColor: 'divider', pr: 6 }}
            >
              <ListItemText
                primary={
                  <Chip
                    label={lab.name}
                    size="small"
                    sx={{
                      fontWeight: 600,
                      bgcolor: lab.color || '#64748b',
                      color: labelTextColor(lab.color)
                    }}
                  />
                }
              />
            </ListItem>
          ))}
        </List>
      )}
      <Button size="small" onClick={load} disabled={loading || removing != null} sx={{ mt: 1 }}>
        Refresh
      </Button>
    </Box>
  );
};

export default PartLabelsManager;
